/**
 * La tasa del BCV, sola.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  NO ES UNA MONEDA NUEVA. ES UNA CASILLA EN LA QUE YA TIENES
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La idea que sale sola es crear una moneda «dólar BCV» al lado del dólar. Se
 * descartó: sería el mismo dólar dos veces, y a partir de ahí hay que decidir
 * en cuál se cobra, cuál se imprime en el recibo y cuál cuenta en los reportes.
 *
 * Lo que el BCV publica son dos números —cuántos bolívares vale un dólar y
 * cuántos un euro—, y con eso se calcula la tasa en cualquier dirección. Por
 * eso esto es un interruptor en la moneda que ya existe, y funciona igual con
 * el dólar, el euro o el bolívar como moneda de los precios.
 *
 * Ver `backend/app/modules/currencies/bcv.py`, que hace la cuenta.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Landmark } from "lucide-react";
import { cx } from "@/components/ui";
import { mensajeDeError } from "@/lib/api";
import { monedas } from "@/lib/endpoints";
import { formatRate } from "@/lib/money";
import type { TenantCurrency } from "@/lib/types";
import { aviso } from "@/stores/ui";

/** Si a este negocio le sirve de algo el BCV. */
export function usaElBCV(lista: TenantCurrency[]): boolean {
  return lista.some((m) => m.puede_tasa_automatica);
}

function comoSeLeeLaFecha(fecha: string): string {
  const [, mes, dia] = fecha.split("-");
  return dia && mes ? `${dia}/${mes}` : fecha;
}

/**
 * El bloque de arriba: qué tasa del BCV se está usando y, a la derecha, si se
 * usa la de mañana en cuanto sale.
 *
 * Solo sale si alguna moneda del negocio puede seguirlo. A una bodega con
 * pesos colombianos no se le enseña algo que no puede usar.
 *
 * ⚠️ Aquí había un botón «Actualizar ahora». Lo quitó el dueño: la tasa se
 * actualiza sola, y un botón para actualizarla hace pensar justo lo contrario.
 */
export function BloqueDelBCV({ monedas: lista }: { monedas: TenantCurrency[] }) {
  const qc = useQueryClient();
  const { data: oficial } = useQuery({
    queryKey: ["tasa-bcv"],
    queryFn: monedas.tasaBCV,
    enabled: usaElBCV(lista),
    // Cambia un par de veces al día: no hace falta preguntarla en cada pantalla.
    staleTime: 10 * 60 * 1000,
  });

  const cambiarManana = useMutation({
    mutationFn: (usar: boolean) => monedas.usarLaDeManana(usar),
    onSuccess: (nuevas, usar) => {
      qc.setQueryData(["monedas-negocio"], nuevas);
      void qc.invalidateQueries({ queryKey: ["tasa-bcv"] });
      void qc.invalidateQueries({ queryKey: ["tasas-vigentes"] });
      const salio = oficial?.siguiente;
      aviso.exito(
        usar ? "Se aplica la próxima tasa" : "Se aplica la tasa del día",
        usar
          ? salio
            ? `El BCV ya publicó la del ${comoSeLeeLaFecha(salio.fecha)} y tus precios ya la aplican.`
            : "El BCV la publica en la tarde; tus precios la aplicarán apenas salga."
          : "La próxima tasa se aplicará al comenzar su fecha.",
      );
    },
    onError: (e) => aviso.error(mensajeDeError(e)),
  });

  if (!usaElBCV(lista)) return null;
  const automaticas = lista.filter((m) => m.tasa_automatica);
  const deManana = oficial?.la_de_manana ?? false;
  const salio = oficial?.siguiente ?? null;
  // La que están usando los precios: la de mañana si ya salió y se pidió.
  const enUso = oficial ? (deManana && salio ? salio : oficial) : null;

  return (
    <div className="mb-4 rounded-[var(--radius-control)] border border-line bg-surface-2 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-3">
        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-accent-soft text-accent">
          <Landmark size={16} />
        </span>
        {/* `basis-56`: sin un ancho mínimo, en el celular el texto se aplastaba en
            una columna de una palabra en vez de dejar bajar al interruptor. */}
        <div className="min-w-0 flex-1 basis-56">
          <p className="text-sm font-medium text-ink">
            {enUso ? (
              <>
                BCV del {comoSeLeeLaFecha(enUso.fecha)}:{" "}
                <span className="tabular">Bs {formatRate(enUso.usd)}</span> por dólar
                {enUso.eur && (
                  <>
                    {" · "}
                    <span className="tabular">Bs {formatRate(enUso.eur)}</span> por euro
                  </>
                )}
              </>
            ) : (
              "Tasa oficial del BCV"
            )}
          </p>
          <p className="text-xs text-muted">
            {automaticas.length > 0
              ? `${automaticas.map((m) => m.currency_code).join(", ")} se actualiza${automaticas.length > 1 ? "n" : ""} sola${automaticas.length > 1 ? "s" : ""}, todos los días.`
              : "Enciende «Automática» en la moneda que quieras y deja de escribir la tasa cada mañana."}
          </p>
        </div>
        {/* Sin monedas automáticas no hay nada que adelantar. */}
        {automaticas.length > 0 && oficial && (
          <InterruptorDeManana
            activo={deManana}
            ocupado={cambiarManana.isPending}
            onChange={(usar) => cambiarManana.mutate(usar)}
          />
        )}
      </div>

      {automaticas.length > 0 && oficial && (salio || deManana) && (
        <p className="mt-2 border-t border-line pt-2 text-xs text-muted">
          {salio && !deManana && (
            <>
              El BCV ya publicó la tasa del {comoSeLeeLaFecha(salio.fecha)}:{" "}
              <span className="tabular font-medium text-ink">Bs {formatRate(salio.usd)}</span> por
              dólar. Se aplicará a tus precios en esa fecha.
            </>
          )}
          {salio && deManana && (
            <>
              Es la tasa que el BCV publicó para el {comoSeLeeLaFecha(salio.fecha)}. La del día (
              {comoSeLeeLaFecha(oficial.fecha)}) es de{" "}
              <span className="tabular">Bs {formatRate(oficial.usd)}</span>.
            </>
          )}
          {!salio && deManana && (
            <>El BCV publica la próxima tasa en la tarde; tus precios la aplicarán apenas salga.</>
          )}
        </p>
      )}
    </div>
  );
}

/**
 * «Aplicar la próxima tasa»: el interruptor del recuadro.
 *
 * ⚠️ Decía «Usar la de mañana / en cuanto el BCV la publica», y el dueño lo
 * vio muy informal al lado de la tasa oficial. Ahora nombra lo que es.
 *
 * El BCV publica en la tarde la tasa del próximo día hábil. Hay negocios que
 * la usan en cuanto sale y otros que esperan a que empiece su día; lo pidió
 * un cliente del dueño, como la «próxima actualización» de las apps de tasas.
 *
 * Es la misma pista y el mismo círculo que `Interruptor` (ver `ui.tsx`), más
 * pequeños y con su nombre al lado, para que quepan a la derecha del recuadro.
 */
function InterruptorDeManana({
  activo,
  ocupado,
  onChange,
}: {
  activo: boolean;
  ocupado: boolean;
  onChange: (valor: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={activo}
      aria-label="Aplicar la próxima tasa al publicarla el BCV"
      disabled={ocupado}
      onClick={() => onChange(!activo)}
      className={cx(
        "ml-auto flex shrink-0 items-center gap-3 rounded-full border py-1.5 pl-3.5 pr-2 text-left transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
        "disabled:cursor-wait disabled:opacity-60",
        activo ? "border-accent/40 bg-accent-soft" : "border-line bg-surface hover:border-accent/40",
      )}
    >
      <span className="min-w-0">
        <span className={cx("block text-xs font-semibold", activo ? "text-accent" : "text-ink")}>
          Aplicar la próxima tasa
        </span>
        <span className="block text-[11px] leading-tight text-muted">
          al publicarla el BCV
        </span>
      </span>
      {/*
        Pista de 36px, círculo de 16px y 2px de margen a cada lado: 16px de
        recorrido, que es `translate-x-4` (entero: ver la nota en `Interruptor`).
      */}
      <span
        className={cx(
          "relative h-5 w-9 shrink-0 rounded-full transition-colors",
          activo ? "bg-accent" : "bg-line-strong",
        )}
      >
        <span
          className={cx(
            "absolute left-0.5 top-0.5 size-4 rounded-full bg-white shadow-sm transition-transform duration-150",
            activo ? "translate-x-4" : "translate-x-0",
          )}
        />
      </span>
    </button>
  );
}

/**
 * El interruptor de una moneda: «esta tasa la pone el BCV».
 *
 * Al encenderlo se trae la tasa en el momento, sin esperar a la tarea de la
 * hora: si no, el dueño enciende la casilla, ve la tasa vieja y piensa que no
 * funcionó.
 */
export function InterruptorDelBCV({ moneda }: { moneda: TenantCurrency }) {
  const qc = useQueryClient();

  const cambiar = useMutation({
    mutationFn: async (encender: boolean) => {
      const lista = await monedas.configurar(moneda.currency_code, { tasa_automatica: encender });
      return encender ? await monedas.actualizarConBCV() : lista;
    },
    onSuccess: (lista, encender) => {
      qc.setQueryData(["monedas-negocio"], lista);
      void qc.invalidateQueries({ queryKey: ["tasas-vigentes"] });
      void qc.invalidateQueries({ queryKey: ["tasa-bcv"] });
      aviso.exito(
        encender ? `${moneda.currency_code} sigue al BCV` : `${moneda.currency_code} vuelve a ser tuya`,
        encender
          ? "Se actualiza sola todos los días. Puedes apagarla cuando quieras."
          : "La tasa la escribes tú otra vez.",
      );
    },
    onError: (e) => aviso.error(mensajeDeError(e)),
  });

  if (!moneda.puede_tasa_automatica) return null;

  return (
    <button
      type="button"
      onClick={() => cambiar.mutate(!moneda.tasa_automatica)}
      disabled={cambiar.isPending}
      aria-pressed={moneda.tasa_automatica}
      title={
        moneda.tasa_automatica
          ? `La tasa de ${moneda.currency_code} la pone el BCV. Tócalo para escribirla tú.`
          : `Que la tasa de ${moneda.currency_code} la ponga el BCV todos los días.`
      }
      className={cx(
        "mb-2.5 shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-60",
        moneda.tasa_automatica
          ? "border-success/40 bg-success-soft text-success"
          : "border-line text-muted hover:border-accent hover:text-accent",
      )}
    >
      {moneda.tasa_automatica ? "BCV automático" : "Automática"}
    </button>
  );
}
